import { call, put, select } from "redux-saga/effects";

import { apiFetch } from "common/utilities";
import { logger } from "common/utilities/logger";
import { applyKnowledgeDataSearchCriteria } from "domains/knowledgeData/actions";
import { getKnowledgeDataSearchCriteria } from "domains/knowledgeData/selectors";
import { createKnowledgeFeedForTopicSelector, getLatestSearchCriteria } from "domains/knowledgeFeed/selectors";
import {
  fetchTopicPinnedKnowledge,
  rejectTopicKnowledgeFulfilled,
  rejectTopicKnowledgeRejected,
} from "domains/pinAddReject/actions";
import type { RejectTopicKnowledge } from "domains/pinAddReject/actionTypes";
import { getPinnedKnowledgePaginationState } from "domains/pinAddReject/selectors";
import { applyPublicationSearchCriteria } from "domains/publication/actions";
import { getPublicationSearchCriteria } from "domains/publication/selectors";
import { getTmSelectedId } from "domains/transformationMap/selectors";
import { applyVideoSearchCriteria } from "domains/videos/actions";
import { getVideoSearchCriteria } from "domains/videos/selectors";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { KnowledgeType } from "entities/knowledge/types";
import { fetchKnowledgeFeedForTopic } from "entities/knowledgeFeed/actions";
import { router } from "router/router";

function* refetchPinnedKnowledge(currentTopicId: string | undefined, limit: number, page: number) {
  if (currentTopicId)
    yield put(
      fetchTopicPinnedKnowledge({
        topicId: currentTopicId,
        limit,
        page,
      }),
    );
}

function* refetchBrowseKnowledgeFeed(knowledgeType: KnowledgeType) {
  switch (knowledgeType) {
    case KnowledgeType.publication: {
      const publicationSearchCriteria: ReturnType<typeof getPublicationSearchCriteria> =
        yield select(getPublicationSearchCriteria);
      yield put(
        applyPublicationSearchCriteria(
          {
            limit: publicationSearchCriteria.limit + publicationSearchCriteria.offset,
            offset: 0,
          },
          { forceRefresh: true },
        ),
      );
      break;
    }

    case KnowledgeType.video: {
      const videoSearchCriteria: ReturnType<typeof getVideoSearchCriteria> = yield select(getVideoSearchCriteria);
      yield put(
        applyVideoSearchCriteria(
          { limit: videoSearchCriteria.limit + videoSearchCriteria.offset, offset: 0 },
          { forceRefresh: true },
        ),
      );
      break;
    }

    case KnowledgeType.data: {
      const knowledgeDataSearchCriteria: ReturnType<typeof getKnowledgeDataSearchCriteria> =
        yield select(getKnowledgeDataSearchCriteria);
      yield put(
        applyKnowledgeDataSearchCriteria(
          {
            limit: knowledgeDataSearchCriteria.limit + knowledgeDataSearchCriteria.offset,
            offset: 0,
          },
          { forceRefresh: true },
        ),
      );
    }
  }
}

function* refetchLatestKnowledgeFeed(topicId: string) {
  const searchCriteria: ReturnType<typeof getLatestSearchCriteria> = yield select(getLatestSearchCriteria);
  const tmSelectedId: ReturnType<typeof getTmSelectedId> = yield select(getTmSelectedId);

  const knowledgeFeedForTopic: ReturnType<ReturnType<typeof createKnowledgeFeedForTopicSelector>> = yield select(
    createKnowledgeFeedForTopicSelector(topicId),
  );

  const knowledge: ReturnType<ReturnType<typeof createKnowledgesSelector>> = yield select(
    createKnowledgesSelector(knowledgeFeedForTopic?.data ?? []),
  );

  yield put(
    fetchKnowledgeFeedForTopic({
      ...searchCriteria,
      id: topicId,
      limit: knowledge.length,
      offset: 0,
      secondaryTopicId: tmSelectedId,
    }),
  );
}

export function* rejectTopicKnowledgeSaga(action: RejectTopicKnowledge) {
  const { knowledgeId, knowledgeType, onSuccessPath, rejectionReason, topicId } = action.meta;
  const currentPinnedKnowledgeState: ReturnType<typeof getPinnedKnowledgePaginationState> = yield select(
    getPinnedKnowledgePaginationState,
  );
  try {
    yield apiFetch("knowledge/reject", {
      method: "PUT",
      body: {
        knowledge_id: knowledgeId,
        reason: rejectionReason,
      },
    });

    yield put(rejectTopicKnowledgeFulfilled(action.meta));
    if (onSuccessPath !== undefined) yield router.navigate(onSuccessPath);

    yield call(
      refetchPinnedKnowledge,
      currentPinnedKnowledgeState.currentTopicId,
      currentPinnedKnowledgeState.totalCount,
      1,
    );
    yield call(refetchBrowseKnowledgeFeed, knowledgeType);
    yield call(refetchLatestKnowledgeFeed, topicId);
  } catch (error) {
    logger.error("[rejectTopicKnowledgeSaga] Error occurred while rejecting knowledge", error);
    yield put(rejectTopicKnowledgeRejected({ ...action.meta, logoutOnAuthError: true }));
  }
}
